/**
 * Compliance-manual engine — the standard private-fund policies & procedures
 * model + deterministic, firm-tailored drafting (Master Plan Argus: the
 * AI-powered compliance manual builder, ADR-0007).
 *
 * Pure and dependency-free (no DB, no `new Date()`): given a small, labeled
 * FIRM PROFILE, compute which of the ~18 standard sections a Rule 206(4)-7
 * manual must contain, TAILOR each one to the firm, and flag the firm-specific
 * facts Argus cannot know with inline `[CCO: …]` markers. The section MODEL
 * lives here, not in the schema (ADR-0007), so the Form ADV Part 2A brochure
 * reuses the same `compliance_documents` substrate by adding a model, not a
 * migration.
 *
 * Provenance, mirroring the filings engine (ADR-0006), at the SECTION grain:
 *   auto   — templated from platform data (the Side Letters & MFN section
 *            describes Argus's own administration of those obligations)
 *   ai     — Claude drafts the tailored prose; a human accepts or edits it.
 *            `tailorManual` seeds a deterministic starting draft so the manual
 *            is never empty offline; the live model only enriches it.
 *   manual — reserved for firm-authored sections Argus did not seed.
 *
 * IMPORTANT: a generated manual is a DRAFTING AID, never legal advice and never
 * "ready to adopt". Every section lands `unreviewed` for CCO sign-off, and the
 * manual is adoptable only when every applicable section is reviewed
 * (build doc §1.4). Tailoring is mandatory — off-the-shelf P&P do not satisfy
 * Rule 206(4)-7.
 */

import type { FundStrategy } from "./canonical";

export const MANUAL_MODEL_VERSION = "manual-v1.0";

export type DocumentType = "compliance_manual" | "adv_2a_brochure";

export type SectionDerivation = "auto" | "ai" | "manual";

export type SectionApplicability = "applicable" | "recommended" | "not_applicable";

export type SectionReviewStatus = "unreviewed" | "accepted" | "edited" | "flagged";

/**
 * Labeled firm-profile intake. A friendly form populates this (never raw JSON);
 * every field flips at least one section's applicability or tailors its prose.
 * Rule 206(4)-7 requires the manual be tailored to the firm.
 */
export interface FirmProfile {
  firmName: string;
  registrationStatus: "sec_registered" | "state_registered" | "exempt_reporting" | "unregistered";
  /** Strategies the firm runs — referenced in the tailored prose. */
  fundStrategies: FundStrategy[];
  /** Drives the Custody Rule (206(4)-2) section. */
  custodyApproach: "qualified_custodian_audit" | "surprise_exam" | "no_custody";
  /** Access persons → Code of Ethics personal-trading reporting (204A-1). */
  hasAccessPersons: boolean;
  /** Government-entity clients/investors → emphasize pay-to-play (206(4)-5). */
  managesGovernmentEntityClients: boolean;
  /** Uses placement agents/solicitors → pay-to-play + marketing nuance. */
  usesPlacementAgents: boolean;
  /** Votes proxies on portfolio holdings → proxy voting (206(4)-6). */
  votesProxies: boolean;
  /** Trades public securities → best execution + emphasized MNPI controls. */
  tradesPublicSecurities: boolean;
  /** Has natural-person clients → privacy notice (Reg S-P). */
  hasNaturalPersonClients: boolean;
  /** Markets to EU/EEA investors → AIFMD marketing nuance + GDPR note. */
  marketsToEuInvestors: boolean;
}

/** A reasonable default profile for a US PE/VC adviser (seeds the intake form). */
export function defaultFirmProfile(firmName: string): FirmProfile {
  return {
    firmName,
    registrationStatus: "sec_registered",
    fundStrategies: ["buyout"],
    custodyApproach: "qualified_custodian_audit",
    hasAccessPersons: true,
    managesGovernmentEntityClients: false,
    usesPlacementAgents: false,
    votesProxies: true,
    tradesPublicSecurities: false,
    hasNaturalPersonClients: false,
    marketsToEuInvestors: false,
  };
}

export interface ManualSectionDef {
  key: string;
  heading: string;
  /** The rule citation, shown as provenance in the UI. */
  regRef: string;
  /** One-line description of what the section governs. */
  summary: string;
  derivation: SectionDerivation;
  /** Links the section to a `regulatory_catalog` key (calendar tie-in), if any. */
  catalogKey?: string;
  /** How this section applies to a given firm. */
  applies: (p: FirmProfile) => SectionApplicability;
  /** Deterministic, firm-tailored starting draft — the offline fallback the
   * draft-manual edge function also computes; the live model enriches it. */
  draft: (p: FirmProfile) => string;
}

/* --------------------------------------------------------------- prose helpers */

function strategyPhrase(p: FirmProfile): string {
  const map: Record<FundStrategy, string> = {
    buyout: "private equity (buyout)",
    growth: "growth equity",
    venture: "venture capital",
    credit: "private credit",
    real_estate: "real estate",
    infrastructure: "infrastructure",
    fund_of_funds: "fund-of-funds",
    other: "private fund",
  };
  const phrases = [...new Set(p.fundStrategies.map((s) => map[s] ?? "private fund"))];
  if (phrases.length <= 1) return phrases[0] ?? "private fund";
  const last = phrases[phrases.length - 1];
  return `${phrases.slice(0, -1).join(", ")} and ${last}`;
}

const ALWAYS = (): SectionApplicability => "applicable";

/* ------------------------------------------------------------- section model */

export const MANUAL_SECTIONS: readonly ManualSectionDef[] = [
  {
    key: "compliance_program",
    heading: "Compliance Program & Annual Review",
    regRef: "Advisers Act Rule 206(4)-7",
    summary:
      "Adoption of written policies, designation of the CCO, and the documented annual review.",
    derivation: "ai",
    catalogKey: "annual_compliance_review",
    applies: ALWAYS,
    draft: (p) =>
      `${p.firmName} ("the Adviser") has adopted these written policies and procedures reasonably designed to prevent, detect, and correct violations of the Investment Advisers Act of 1940 and the rules thereunder, as required by Rule 206(4)-7. ` +
      `The Adviser has designated a Chief Compliance Officer ([CCO: insert name and title]) responsible for administering this program. ` +
      `The CCO conducts and documents a review of the adequacy and effectiveness of these policies no less than annually, and more frequently in response to material changes in the Adviser's business, applicable regulation, or compliance issues identified during the year. ` +
      `The annual review and any resulting amendments are recorded in the manual's change log and retained under the Adviser's books-and-records policy. ` +
      `[CCO: confirm the date of the most recent annual review and the management approval process.]`,
  },
  {
    key: "code_of_ethics",
    heading: "Code of Ethics",
    regRef: "Advisers Act Rule 204A-1",
    summary:
      "Standards of business conduct, fiduciary duty, and the annual acknowledgement requirement.",
    derivation: "ai",
    catalogKey: "code_of_ethics_annual_attestation",
    applies: ALWAYS,
    draft: () =>
      `The Adviser maintains a Code of Ethics setting the standard of business conduct expected of all supervised persons, grounded in the Adviser's fiduciary duty to its clients. ` +
      `All supervised persons must comply with applicable federal securities laws and must promptly report violations of this Code to the CCO. ` +
      `Each supervised person receives a copy of the Code (and any amendments) and provides a written acknowledgement of receipt and compliance upon hire and at least annually. ` +
      `[CCO: confirm how acknowledgements are distributed and tracked, and the population of supervised persons.]`,
  },
  {
    key: "personal_trading",
    heading: "Personal Securities Trading & Reporting",
    regRef: "Advisers Act Rule 204A-1(b)",
    summary:
      "Access-person holdings and transaction reporting, pre-clearance, and restricted lists.",
    derivation: "ai",
    catalogKey: "coe_transaction_report_quarterly",
    applies: (p) => (p.hasAccessPersons ? "applicable" : "recommended"),
    draft: (p) =>
      `Access persons of the Adviser must submit an initial and annual holdings report (current within 45 days) and quarterly transaction reports within 30 days of each quarter-end. ` +
      `${p.tradesPublicSecurities ? "Because the Adviser transacts in public securities, personal trades in reportable securities require pre-clearance by the CCO and are screened against the Adviser's restricted list." : "Given the Adviser's private-markets focus, reportable-securities activity is limited; the CCO nonetheless monitors personal trading for conflicts with client transactions."} ` +
      `Reports are reviewed by the CCO for conflicts with client activity and retained under the books-and-records policy. ` +
      `[CCO: identify the access persons and the reporting/pre-clearance system used.]`,
  },
  {
    key: "insider_trading_mnpi",
    heading: "Material Non-Public Information & Insider Trading",
    regRef: "Advisers Act Section 204A",
    summary: "Controls to prevent misuse of MNPI, information barriers, and expert-network use.",
    derivation: "ai",
    applies: ALWAYS,
    draft: (p) =>
      `The Adviser maintains and enforces written policies reasonably designed to prevent the misuse of material non-public information ("MNPI") by the Adviser or any associated person, as required by Section 204A. ` +
      `Supervised persons who come into possession of MNPI must not trade on it or communicate it improperly and must notify the CCO. ` +
      `${p.tradesPublicSecurities ? "The Adviser maintains information barriers, a restricted list, and watch-list procedures appropriate to its public-securities activity." : "While the Adviser primarily holds private positions, deal diligence and board involvement can generate MNPI; the CCO administers wall-crossing and restricted-list procedures accordingly."} ` +
      `[CCO: describe the use of expert networks and any information-barrier arrangements.]`,
  },
  {
    key: "valuation",
    heading: "Valuation",
    regRef: "Advisers Act Rule 206(4)-7 (fiduciary duty); ASC 820",
    summary: "Fair-value methodology, the valuation committee, and conflict mitigation.",
    derivation: "ai",
    applies: ALWAYS,
    draft: (p) =>
      `The Adviser values fund assets in accordance with each fund's governing documents and a consistent fair-value methodology (ASC 820). ` +
      `Because the Adviser earns asset-based fees and carried interest, valuation presents an inherent conflict; the Adviser mitigates it through a valuation committee, documented methodologies, and (where applicable) third-party pricing or an independent annual audit. ` +
      `${strategyPhrase(p) ? `The Adviser's ${strategyPhrase(p)} holdings are valued using methodologies appropriate to those asset types.` : ""} ` +
      `[CCO: identify the valuation committee members, valuation cadence, and any third-party valuation provider.]`,
  },
  {
    key: "custody",
    heading: "Custody",
    regRef: "Advisers Act Rule 206(4)-2",
    summary: "Custody Rule compliance, the audit exception, and surprise-examination logic.",
    derivation: "ai",
    catalogKey: "custody_rule_audit",
    applies: ALWAYS,
    draft: (p) => {
      const approach =
        p.custodyApproach === "qualified_custodian_audit"
          ? `The Adviser relies on the audit exception: each pooled vehicle is subject to an annual GAAP financial-statement audit by a PCAOB-registered independent accountant, with audited financials distributed to investors within 120 days of fiscal year-end (180 days for fund-of-funds).`
          : p.custodyApproach === "surprise_exam"
            ? `The Adviser satisfies the Custody Rule through an annual surprise examination by an independent public accountant, in addition to maintaining client assets with a qualified custodian.`
            : `The Adviser has structured its arrangements to limit custody; the CCO confirms the basis for any custody determination at least annually.`;
      return (
        `The Adviser is deemed to have custody of client assets as a general partner / managing member of the funds it advises and complies with Rule 206(4)-2. ` +
        `${approach} ` +
        `[CCO: identify each fund's qualified custodian and auditor and confirm distribution timing.]`
      );
    },
  },
  {
    key: "marketing",
    heading: "Marketing & Advertising",
    regRef: "Advisers Act Rule 206(4)-1",
    summary:
      "Marketing Rule compliance: substantiation, fair-and-balanced, performance, testimonials.",
    derivation: "ai",
    catalogKey: "marketing_rule_review",
    applies: ALWAYS,
    draft: (p) =>
      `All advertisements and marketing communications are reviewed and approved by the CCO (or designee) before use for compliance with the Marketing Rule, including the general prohibitions, substantiation of factual claims, fair-and-balanced presentation, and performance-presentation requirements (net/gross, prescribed time periods, and required disclosures). ` +
      `${p.usesPlacementAgents ? "Placement agents and solicitors are treated as promoters subject to the Rule's disclosure, oversight, and written-agreement requirements." : ""} ` +
      `${p.marketsToEuInvestors ? "Marketing to EU/EEA investors is additionally conducted in accordance with the applicable AIFMD national private-placement regime." : ""} ` +
      `Marketing materials and the basis for performance figures are retained under the books-and-records policy. ` +
      `[CCO: describe the marketing-review workflow and approval records.]`,
  },
  {
    key: "pay_to_play",
    heading: "Political Contributions (Pay-to-Play)",
    regRef: "Advisers Act Rule 206(4)-5",
    summary: "Covered-associate contribution pre-clearance and the de-minimis screen.",
    derivation: "ai",
    catalogKey: "political_contributions_review",
    applies: (p) =>
      p.managesGovernmentEntityClients || p.usesPlacementAgents ? "applicable" : "recommended",
    draft: (p) =>
      `The Adviser maintains pay-to-play controls under Rule 206(4)-5. Covered associates must pre-clear political contributions; the CCO screens each against the de-minimis limits ($350 per election where the contributor may vote for the official, $150 otherwise) and the two-year look-back ban on receiving compensation for advisory services to a government-entity client. ` +
      `${p.managesGovernmentEntityClients ? "Because the Adviser has government-entity investors/clients, this policy is administered with heightened diligence." : "The Adviser does not currently advise government-entity clients but maintains these controls prophylactically."} ` +
      `${p.usesPlacementAgents ? "Third parties soliciting government entities on the Adviser's behalf must be registered and subject to the Rule." : ""} ` +
      `[CCO: identify covered associates and the contribution pre-clearance log.]`,
  },
  {
    key: "allocation",
    heading: "Investment & Trade Allocation",
    regRef: "Advisers Act Rule 206(4)-7 (fiduciary duty)",
    summary: "Fair allocation of investment opportunities and expenses across funds/co-invest.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `The Adviser allocates investment opportunities among its funds, parallel vehicles, and co-investors fairly and consistently with each vehicle's mandate, governing documents, and any allocation policy, and does not systematically advantage one client over another or itself. ` +
      `Co-investment allocations follow a documented framework, and exceptions are approved and recorded by the CCO. ` +
      `[CCO: describe the co-investment allocation framework and any allocation priority among funds.]`,
  },
  {
    key: "conflicts_of_interest",
    heading: "Conflicts of Interest",
    regRef: "Advisers Act Section 206 (fiduciary duty)",
    summary: "Identification, disclosure, and mitigation of conflicts; related-party transactions.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `As a fiduciary, the Adviser identifies, mitigates, and fully and fairly discloses material conflicts of interest so that clients can provide informed consent, consistent with its Form ADV Part 2A disclosures. ` +
      `Conflicts include fee and compensation arrangements, cross-transactions and principal transactions, affiliated service providers, co-investment allocation, and the use of fund assets. ` +
      `Principal and cross transactions are conducted only with the disclosures and consents required by Section 206(3). ` +
      `[CCO: list the firm's affiliated service providers and recurring related-party arrangements.]`,
  },
  {
    key: "best_execution",
    heading: "Best Execution",
    regRef: "Advisers Act Section 206 (fiduciary duty)",
    summary: "Seeking best execution for client transactions; broker selection and soft dollars.",
    derivation: "ai",
    applies: (p) => (p.tradesPublicSecurities ? "applicable" : "recommended"),
    draft: (p) =>
      `${p.tradesPublicSecurities ? "The Adviser seeks best execution for client transactions, periodically evaluating the execution quality, commission rates, and services of the brokers it uses, and documents that review." : "The Adviser's investments are predominantly privately negotiated, so traditional best-execution analysis has limited application; where the Adviser transacts in public securities it seeks best execution and documents broker selection."} ` +
      `Any soft-dollar arrangements are limited to research and brokerage within the Section 28(e) safe harbor and disclosed in Form ADV. ` +
      `[CCO: identify executing brokers and any soft-dollar arrangements.]`,
  },
  {
    key: "proxy_voting",
    heading: "Proxy Voting",
    regRef: "Advisers Act Rule 206(4)-6",
    summary: "Voting client securities in clients' best interest and conflict handling.",
    derivation: "ai",
    applies: (p) => (p.votesProxies ? "applicable" : "not_applicable"),
    draft: (p) =>
      p.votesProxies
        ? `Where the Adviser has authority to vote securities held by client accounts, it votes in the best interest of clients pursuant to written proxy-voting procedures, addresses conflicts of interest in voting, maintains voting records, and provides clients a copy of the procedures and voting record on request (Rule 206(4)-6).` +
          ` [CCO: confirm whether voting is conducted in-house or via a proxy advisor.]`
        : `The Adviser does not exercise voting authority over client securities and therefore does not maintain a proxy-voting policy under Rule 206(4)-6. [CCO: confirm this remains accurate.]`,
  },
  {
    key: "books_and_records",
    heading: "Books & Records",
    regRef: "Advisers Act Rule 204-2",
    summary: "Required records, retention periods, and electronic-records controls.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `The Adviser makes and keeps the books and records required by Rule 204-2, including required financial, advisory, marketing, personal-trading, and compliance records, for the prescribed retention periods (generally five years, the first two in an easily accessible place), and maintains records in a non-rewriteable, non-erasable format where required. ` +
      `[CCO: identify the records-retention system and the records custodian.]`,
  },
  {
    key: "business_continuity",
    heading: "Business Continuity & Transition Plan",
    regRef: "Advisers Act Rule 206(4)-7 (fiduciary duty)",
    summary: "BCP for disruptions and a transition plan for key-person/wind-down events.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `The Adviser maintains a business-continuity and transition plan reasonably designed to enable it to continue operations, protect client assets and data, and minimize client harm during a significant business disruption, and to transition client accounts in the event of the Adviser's wind-down or a key-person event. ` +
      `The plan addresses data backup and recovery, alternate facilities and communications, and critical third-party vendors, and is tested periodically. ` +
      `[CCO: identify key persons, critical vendors, and the most recent BCP test date.]`,
  },
  {
    key: "cybersecurity",
    heading: "Cybersecurity & Information Security",
    regRef: "Advisers Act Rule 206(4)-7; Reg S-P safeguards",
    summary: "Safeguards for systems and client data, incident response, and vendor diligence.",
    derivation: "ai",
    applies: (p) =>
      p.hasNaturalPersonClients || p.marketsToEuInvestors ? "applicable" : "recommended",
    draft: (p) =>
      `The Adviser maintains an information-security program with administrative, technical, and physical safeguards reasonably designed to protect its systems and confidential client information, including access controls, encryption, monitoring, employee training, an incident-response plan, and diligence over service providers with access to data. ` +
      `${p.marketsToEuInvestors ? "Where the Adviser processes personal data of EU/EEA individuals, the program is administered consistently with applicable data-protection law (GDPR)." : ""} ` +
      `[CCO: identify the security owner, the incident-response escalation path, and key technology vendors.]`,
  },
  {
    key: "aml",
    heading: "Anti-Money Laundering & Sanctions",
    regRef: "Bank Secrecy Act; OFAC; FinCEN investment-adviser rule",
    summary: "Investor diligence, sanctions screening, and suspicious-activity escalation.",
    derivation: "ai",
    applies: () => "recommended",
    draft: () =>
      `The Adviser conducts risk-based investor due diligence and OFAC/sanctions screening at subscription and on an ongoing basis, escalates and documents suspicious activity, and (as the FinCEN investment-adviser AML rule and the Adviser's administrators require) maintains procedures appropriate to its investor base. ` +
      `[CCO: confirm whether AML/KYC is performed in-house or by the fund administrator, and identify the AML officer.]`,
  },
  {
    key: "gifts_entertainment",
    heading: "Gifts & Entertainment",
    regRef: "Advisers Act Section 206; Rule 206(4)-5 coordination",
    summary: "Limits, reporting, and pre-clearance for gifts and business entertainment.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `Supervised persons may neither give nor accept gifts or entertainment that could compromise, or appear to compromise, the Adviser's objectivity or a client's interests. ` +
      `Gifts above a de-minimis threshold and significant entertainment must be reported to, and where required pre-cleared by, the CCO; the policy is coordinated with the pay-to-play rules to capture anything of value provided to government officials. ` +
      `[CCO: set the de-minimis gift threshold and the reporting/log mechanism.]`,
  },
  {
    key: "expense_allocation",
    heading: "Expense Allocation",
    regRef: "Advisers Act Section 206 (fiduciary duty); fund governing documents",
    summary: "Allocating fund vs. adviser expenses and shared expenses consistently with the LPA.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `The Adviser allocates expenses between itself and the funds, and among funds and co-investors, consistently with each fund's governing documents and a documented expense-allocation policy, and discloses its expense practices in Form ADV Part 2A. ` +
      `Shared expenses (including broken-deal expenses, where permitted) are allocated on a fair and consistent basis, and the CCO reviews allocations periodically. ` +
      `[CCO: describe the treatment of broken-deal expenses and any expenses borne by co-investors.]`,
  },
  {
    key: "side_letters_mfn",
    heading: "Side Letters & Most-Favored-Nation Administration",
    regRef: "Advisers Act Section 206; Rule 206(4)-7; Private Fund Adviser rules",
    summary: "Tracking side-letter obligations and administering MFN elections — managed in Argus.",
    derivation: "auto",
    applies: ALWAYS,
    draft: () =>
      `The Adviser negotiates side letters with certain investors and administers most-favored-nation ("MFN") provisions so that comparable investors receive the benefits to which they are entitled. ` +
      `The Adviser maintains a complete inventory of side-letter terms and their MFN scope, runs an MFN comparison when new terms are granted, documents which elections are offered and to whom, and tracks the resulting reporting and operational obligations to completion — administered in Argus, which preserves an append-only audit trail of each comparison, election, and obligation. ` +
      `Material conflicts arising from preferential terms are disclosed consistent with the Adviser's fiduciary duty and applicable Private Fund Adviser requirements. ` +
      `[CCO: confirm the MFN election window and notification process under the fund's governing documents.]`,
  },
  {
    key: "privacy",
    heading: "Privacy (Regulation S-P)",
    regRef: "Regulation S-P",
    summary: "Privacy notices and safeguards for non-public personal information.",
    derivation: "ai",
    catalogKey: "privacy_notice_annual",
    applies: (p) => (p.hasNaturalPersonClients ? "applicable" : "recommended"),
    draft: (p) =>
      `The Adviser protects the non-public personal information of natural-person clients and investors, provides an initial and (subject to the FAST Act exception) annual privacy notice, limits disclosure to non-affiliated third parties as required, and maintains safeguards under Regulation S-P. ` +
      `${p.hasNaturalPersonClients ? "Because the Adviser has natural-person clients/investors, privacy notices are delivered and tracked." : "The Adviser's investors are predominantly institutional; it maintains these procedures to the extent natural-person investors are admitted."} ` +
      `[CCO: confirm the natural-person investor population and the privacy-notice delivery method.]`,
  },
] as const;

/* --------------------------------------------------------------- tailoring */

export interface SectionDraftRow {
  sectionKey: string;
  heading: string;
  sortOrder: number;
  regRef: string;
  applicability: SectionApplicability;
  source: SectionDerivation;
  body: string | null;
  isGap: boolean;
  confidence: number | null;
  citation: string | null;
}

export interface ResolvedSection {
  key: string;
  heading: string;
  regRef: string;
  summary: string;
  derivation: SectionDerivation;
  catalogKey: string | undefined;
  applicability: SectionApplicability;
}

/** Resolve a section model's applicability for a firm (no prose). Generic over
 * the model so the manual and the Form ADV Part 2A brochure share the logic. */
export function buildSectionsFromModel(
  model: readonly ManualSectionDef[],
  profile: FirmProfile,
): ResolvedSection[] {
  return model.map((s) => ({
    key: s.key,
    heading: s.heading,
    regRef: s.regRef,
    summary: s.summary,
    derivation: s.derivation,
    catalogKey: s.catalogKey,
    applicability: s.applies(profile),
  }));
}

/** The manual's sections with applicability resolved for a firm (no prose). */
export function buildManualSections(profile: FirmProfile): ResolvedSection[] {
  return buildSectionsFromModel(MANUAL_SECTIONS, profile);
}

/** Map a section key to its `regulatory_catalog` key (calendar tie-in), if any. */
export function sectionCatalogKey(sectionKey: string): string | undefined {
  return MANUAL_SECTIONS.find((s) => s.key === sectionKey)?.catalogKey;
}

/**
 * Produce the initial section rows for a manual, tailored to the firm:
 *   - applicable / recommended sections get the deterministic starting draft
 *     (a real, firm-tailored policy with inline `[CCO: …]` gap markers) — the
 *     manual is never empty offline; "Draft with AI" enriches the `ai` sections;
 *   - not_applicable sections are kept (with no body) for the audit trail of why
 *     they were excluded, and flagged as gaps only if a CCO later marks them
 *     applicable.
 */
export function tailorFromModel(
  model: readonly ManualSectionDef[],
  profile: FirmProfile,
): SectionDraftRow[] {
  return model.map((s, i) => {
    const applicability = s.applies(profile);
    const included = applicability !== "not_applicable";
    const body = included ? s.draft(profile).replace(/\s+/g, " ").trim() : null;
    return {
      sectionKey: s.key,
      heading: s.heading,
      sortOrder: i,
      regRef: s.regRef,
      applicability,
      source: s.derivation,
      body,
      // A section is a gap when it should be in the document but has no prose yet.
      isGap: included && (body === null || body.length === 0),
      confidence: included ? (s.derivation === "auto" ? 1 : 0.6) : null,
      citation: included
        ? s.derivation === "auto"
          ? "Templated from Argus's side-letter / MFN administration."
          : `Tailored starting draft from the firm profile (${s.regRef}).`
        : "Marked not applicable from the firm profile.",
    };
  });
}

export function tailorManual(profile: FirmProfile): SectionDraftRow[] {
  return tailorFromModel(MANUAL_SECTIONS, profile);
}

/** Count the inline `[CCO: …]` markers in a body — the firm-specific gaps the
 * CCO must fill in. Surfaced in the UI as the honest "what Argus can't know". */
export function countInlineGaps(body: string | null | undefined): number {
  if (!body) return 0;
  const m = body.match(/\[CCO:[^\]]*\]/g);
  return m ? m.length : 0;
}

/* ------------------------------------------------------------- completeness */

export interface ManualSectionState {
  applicability: SectionApplicability;
  source: SectionDerivation;
  body: string | null;
  reviewStatus: SectionReviewStatus;
}

export interface ManualCompleteness {
  total: number;
  /** sections that apply (applicable + recommended). */
  applicable: number;
  notApplicable: number;
  /** applicable sections that have prose. */
  drafted: number;
  /** applicable sections a human has signed off (accepted or edited). */
  reviewed: number;
  /** applicable sections flagged for follow-up. */
  flagged: number;
  /** applicable sections with no prose yet. */
  gaps: number;
  /** total inline `[CCO: …]` markers across drafted applicable sections. */
  inlineGaps: number;
  /** % of applicable sections drafted. */
  pct: number;
  /** % of applicable sections reviewed. */
  reviewedPct: number;
}

export function manualCompleteness(
  sections: (ManualSectionState & { body: string | null })[],
): ManualCompleteness {
  const total = sections.length;
  let applicable = 0;
  let notApplicable = 0;
  let drafted = 0;
  let reviewed = 0;
  let flagged = 0;
  let gaps = 0;
  let inlineGaps = 0;

  for (const s of sections) {
    if (s.applicability === "not_applicable") {
      notApplicable += 1;
      continue;
    }
    applicable += 1;
    const hasBody = !!s.body && s.body.trim().length > 0;
    if (hasBody) {
      drafted += 1;
      inlineGaps += countInlineGaps(s.body);
    } else {
      gaps += 1;
    }
    if (s.reviewStatus === "accepted" || s.reviewStatus === "edited") reviewed += 1;
    if (s.reviewStatus === "flagged") flagged += 1;
  }

  return {
    total,
    applicable,
    notApplicable,
    drafted,
    reviewed,
    flagged,
    gaps,
    inlineGaps,
    pct: applicable === 0 ? 0 : Math.round((drafted / applicable) * 100),
    reviewedPct: applicable === 0 ? 0 : Math.round((reviewed / applicable) * 100),
  };
}

/** A manual is adoptable when every applicable section has prose AND has been
 * reviewed (accepted or edited) by a human — AI is never authoritative (§1.4). */
export function isManualAdoptable(
  sections: (ManualSectionState & { body: string | null })[],
): boolean {
  const applicable = sections.filter((s) => s.applicability !== "not_applicable");
  if (applicable.length === 0) return false;
  return applicable.every((s) => {
    const hasBody = !!s.body && s.body.trim().length > 0;
    return hasBody && (s.reviewStatus === "accepted" || s.reviewStatus === "edited");
  });
}

/* ----------------------------------------------------------- version diff */

export interface SnapshotSection {
  sectionKey: string;
  heading: string;
  body: string | null;
  applicability: SectionApplicability;
}

export type ChangeKind = "added" | "removed" | "modified" | "applicability_changed" | "unchanged";

export interface ChangeLogEntry {
  sectionKey: string;
  heading: string;
  change: ChangeKind;
  detail?: string;
}

/**
 * Diff a prior version snapshot against the current sections to produce the
 * change-log entries an annual review (Rule 206(4)-7) records. Pure: a section
 * is "modified" when its body text changed, "applicability_changed" when it was
 * included/excluded, "added"/"removed" when present in only one set.
 */
export function diffSections(
  prev: SnapshotSection[],
  current: SnapshotSection[],
): ChangeLogEntry[] {
  const prevByKey = new Map(prev.map((s) => [s.sectionKey, s]));
  const curByKey = new Map(current.map((s) => [s.sectionKey, s]));
  const entries: ChangeLogEntry[] = [];

  for (const cur of current) {
    const before = prevByKey.get(cur.sectionKey);
    if (!before) {
      entries.push({ sectionKey: cur.sectionKey, heading: cur.heading, change: "added" });
      continue;
    }
    if (before.applicability !== cur.applicability) {
      entries.push({
        sectionKey: cur.sectionKey,
        heading: cur.heading,
        change: "applicability_changed",
        detail: `${before.applicability} → ${cur.applicability}`,
      });
      continue;
    }
    if ((before.body ?? "") !== (cur.body ?? "")) {
      entries.push({ sectionKey: cur.sectionKey, heading: cur.heading, change: "modified" });
    } else {
      entries.push({ sectionKey: cur.sectionKey, heading: cur.heading, change: "unchanged" });
    }
  }
  for (const before of prev) {
    if (!curByKey.has(before.sectionKey)) {
      entries.push({ sectionKey: before.sectionKey, heading: before.heading, change: "removed" });
    }
  }
  return entries;
}

/** A one-line human change summary from a diff (used as the version's note). */
export function summarizeChanges(entries: ChangeLogEntry[]): string {
  const counts = entries.reduce<Record<ChangeKind, number>>(
    (acc, e) => ((acc[e.change] = (acc[e.change] ?? 0) + 1), acc),
    {} as Record<ChangeKind, number>,
  );
  const parts: string[] = [];
  if (counts.added) parts.push(`${counts.added} added`);
  if (counts.modified) parts.push(`${counts.modified} modified`);
  if (counts.applicability_changed) parts.push(`${counts.applicability_changed} re-scoped`);
  if (counts.removed) parts.push(`${counts.removed} removed`);
  return parts.length > 0 ? parts.join(", ") : "No material changes";
}

/* ============================================================ FORM ADV 2A ===
 * Form ADV Part 2A (the client "brochure") — the second document KIND on the
 * authored-document substrate (ADR-0007), a new MODEL not a new migration. The
 * brochure's 18 narrative items are tailored from the same firm profile as the
 * compliance manual, and several items DRAW FROM the manual's policies so the two
 * stay aligned (Item 11 ↔ Code of Ethics; Item 12 ↔ Brokerage; Item 15 ↔ Custody;
 * Item 17 ↔ Proxy Voting). Same honesty mechanic: every item is a tailored draft
 * with inline `[CCO: …]` markers for firm-specific facts Argus cannot know, and is
 * a drafting aid for CCO + counsel review, never a filed brochure.
 * ===========================================================================*/

export const BROCHURE_MODEL_VERSION = "adv-2a-v1.0";

/** Which compliance-manual section(s) each Part 2A item should stay aligned with.
 * Surfaced in the UI so editing a policy flags the brochure item to refresh. */
export const ADV_2A_ALIGNMENT: Record<string, string[]> = {
  adv2a_item_6: ["conflicts_of_interest", "allocation"],
  adv2a_item_8: ["valuation"],
  adv2a_item_10: ["conflicts_of_interest"],
  adv2a_item_11: ["code_of_ethics", "personal_trading", "insider_trading_mnpi"],
  adv2a_item_12: ["best_execution"],
  adv2a_item_14: ["marketing", "pay_to_play"],
  adv2a_item_15: ["custody"],
  adv2a_item_17: ["proxy_voting"],
};

export const ADV_2A_SECTIONS: readonly ManualSectionDef[] = [
  {
    key: "adv2a_item_1",
    heading: "Item 1 — Cover Page",
    regRef: "Form ADV Part 2A Item 1",
    summary: "Firm identity, brochure date, and the required statement.",
    derivation: "ai",
    applies: ALWAYS,
    draft: (p) =>
      `${p.firmName} ("the Adviser"), [CCO: principal office address], [CCO: brochure date]. ` +
      `This brochure provides information about the qualifications and business practices of the Adviser. ` +
      `If you have any questions about the contents of this brochure, please contact the Adviser's Chief Compliance Officer at [CCO: contact details]. ` +
      `The information in this brochure has not been approved or verified by the SEC or any state authority; registration does not imply a certain level of skill or training.`,
  },
  {
    key: "adv2a_item_2",
    heading: "Item 2 — Material Changes",
    regRef: "Form ADV Part 2A Item 2",
    summary: "Summary of material changes since the last annual update.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `This Item summarizes the material changes to this brochure since the Adviser's last annual updating amendment. ` +
      `[CCO: summarize material changes since the prior brochure, or state that there have been no material changes.]`,
  },
  {
    key: "adv2a_item_4",
    heading: "Item 4 — Advisory Business",
    regRef: "Form ADV Part 2A Item 4",
    summary: "Firm description, ownership, services, and assets under management.",
    derivation: "ai",
    applies: ALWAYS,
    draft: (p) =>
      `The Adviser is an investment adviser that provides discretionary investment advisory services to privately offered pooled investment vehicles pursuing ${strategyPhrase(p)} strategies. ` +
      `The Adviser is principally owned by [CCO: list principal owners]. ` +
      `The Adviser tailors advice to each fund's governing documents and investment mandate rather than to the individual needs of underlying investors. ` +
      `As of [CCO: date], the Adviser managed approximately [CCO: regulatory AUM] in regulatory assets under management on a discretionary basis.`,
  },
  {
    key: "adv2a_item_5",
    heading: "Item 5 — Fees and Compensation",
    regRef: "Form ADV Part 2A Item 5",
    summary: "Management fees, carried interest, and fund expenses.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `The Adviser is typically compensated through a management fee and a performance-based allocation (carried interest), as set out in each fund's governing documents. ` +
      `[CCO: state the management fee rate, fee basis, and carried-interest terms.] ` +
      `Funds also bear their own expenses as described in their governing documents; the Adviser's expense-allocation policy governs the allocation of shared and broken-deal expenses. ` +
      `Management fees are generally not refundable for partial periods except as the governing documents provide.`,
  },
  {
    key: "adv2a_item_6",
    heading: "Item 6 — Performance-Based Fees & Side-by-Side Management",
    regRef: "Form ADV Part 2A Item 6",
    summary: "Carried interest as a performance fee and the resulting conflicts.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `The Adviser receives performance-based compensation (carried interest) from the funds it advises. ` +
      `Performance-based compensation creates an incentive to favor accounts that pay it and to make riskier or more speculative investments than would be the case absent such compensation. ` +
      `The Adviser addresses these conflicts through its allocation and conflicts-of-interest policies and its valuation controls. ` +
      `[CCO: describe any side-by-side management of vehicles with differing fee terms.]`,
  },
  {
    key: "adv2a_item_7",
    heading: "Item 7 — Types of Clients",
    regRef: "Form ADV Part 2A Item 7",
    summary: "The pooled vehicles advised and investor eligibility/minimums.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `The Adviser's clients are privately offered pooled investment vehicles whose investors are generally limited to qualified purchasers and/or accredited investors meeting the eligibility requirements of the relevant exemptions. ` +
      `[CCO: state the minimum investment commitment(s) for the funds.]`,
  },
  {
    key: "adv2a_item_8",
    heading: "Item 8 — Methods of Analysis, Strategies & Risk of Loss",
    regRef: "Form ADV Part 2A Item 8",
    summary: "Investment process, strategies, and material risks.",
    derivation: "ai",
    applies: ALWAYS,
    draft: (p) =>
      `The Adviser pursues ${strategyPhrase(p)} strategies, employing fundamental analysis and diligence appropriate to privately negotiated investments. ` +
      `Investing in private funds involves a risk of loss that investors should be prepared to bear, including illiquidity, leverage, concentration, valuation uncertainty, and the long-term nature of the investments. ` +
      `[CCO: describe the material, strategy-specific risks of the funds in more detail.]`,
  },
  {
    key: "adv2a_item_9",
    heading: "Item 9 — Disciplinary Information",
    regRef: "Form ADV Part 2A Item 9",
    summary: "Material legal or disciplinary events (or a statement of none).",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `Registered investment advisers must disclose material facts about any legal or disciplinary event that would be material to a client's evaluation of the Adviser or the integrity of its management. ` +
      `[CCO: state that there are no such disclosable events, or describe them.]`,
  },
  {
    key: "adv2a_item_10",
    heading: "Item 10 — Other Financial Industry Activities & Affiliations",
    regRef: "Form ADV Part 2A Item 10",
    summary: "Affiliated general partners/managers and related-party arrangements.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `The Adviser is affiliated with the general partners / managing members of the funds it advises and may be affiliated with other entities that provide services to the funds. ` +
      `These affiliations create conflicts of interest that the Adviser manages and discloses consistent with its conflicts-of-interest policy. ` +
      `[CCO: list affiliated general partners, management entities, and any affiliated service providers.]`,
  },
  {
    key: "adv2a_item_11",
    heading: "Item 11 — Code of Ethics, Client Transactions & Personal Trading",
    regRef: "Form ADV Part 2A Item 11",
    summary: "Summary of the Code of Ethics and personal-trading controls.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `The Adviser has adopted a Code of Ethics under Rule 204A-1 that sets the standard of business conduct for its supervised persons, requires compliance with the federal securities laws, and governs personal securities trading, reporting, and the handling of material non-public information. ` +
      `A copy of the Code of Ethics is available to any client or prospective client on request. ` +
      `[CCO: describe any participation or interest of the Adviser in client transactions (e.g., principal or cross transactions) and how related conflicts are addressed.]`,
  },
  {
    key: "adv2a_item_12",
    heading: "Item 12 — Brokerage Practices",
    regRef: "Form ADV Part 2A Item 12",
    summary: "Broker selection, best execution, and soft dollars.",
    derivation: "ai",
    applies: ALWAYS,
    draft: (p) =>
      `${p.tradesPublicSecurities ? "Where the Adviser executes transactions in public securities, it seeks best execution and periodically evaluates the brokers it uses." : "The Adviser's investments are predominantly privately negotiated, so traditional brokerage practices have limited application; where the Adviser transacts in public securities it seeks best execution."} ` +
      `Any soft-dollar arrangements are limited to the Section 28(e) safe harbor. ` +
      `[CCO: identify executing brokers, any soft-dollar arrangements, and trade-aggregation practices.]`,
  },
  {
    key: "adv2a_item_13",
    heading: "Item 13 — Review of Accounts",
    regRef: "Form ADV Part 2A Item 13",
    summary: "Who reviews the funds and what investors receive.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `The funds' investments and performance are reviewed periodically by the Adviser's investment professionals, and the Chief Compliance Officer oversees compliance reviews. ` +
      `Investors receive periodic reports and audited annual financial statements as provided in the funds' governing documents. ` +
      `[CCO: state the frequency and nature of investor reporting.]`,
  },
  {
    key: "adv2a_item_14",
    heading: "Item 14 — Client Referrals & Other Compensation",
    regRef: "Form ADV Part 2A Item 14",
    summary: "Placement agents, solicitors, and other compensation arrangements.",
    derivation: "ai",
    applies: ALWAYS,
    draft: (p) =>
      `${p.usesPlacementAgents ? "The Adviser engages placement agents / solicitors to introduce investors to the funds; these arrangements are subject to written agreements and the disclosure and oversight requirements of the Marketing Rule and, where applicable, the pay-to-play rule." : "The Adviser does not currently compensate third parties for investor referrals."} ` +
      `[CCO: describe any economic benefits received from non-clients for providing advisory services, and any referral compensation.]`,
  },
  {
    key: "adv2a_item_15",
    heading: "Item 15 — Custody",
    regRef: "Form ADV Part 2A Item 15",
    summary: "Custody status and the audited-financials delivery.",
    derivation: "ai",
    applies: ALWAYS,
    draft: (p) =>
      `The Adviser is deemed to have custody of fund assets as a result of its affiliation with the funds' general partners / managers. ` +
      `${p.custodyApproach === "qualified_custodian_audit" ? "Fund assets are held with qualified custodians, and each fund is subject to an annual audit by an independent public accountant with audited financial statements distributed to investors, satisfying the Custody Rule's audit provision." : p.custodyApproach === "surprise_exam" ? "The Adviser satisfies the Custody Rule through an annual surprise examination in addition to maintaining assets with qualified custodians." : "The Adviser confirms the basis for any custody determination and the applicable Custody Rule provisions at least annually."} ` +
      `[CCO: identify the qualified custodian(s) and auditor.]`,
  },
  {
    key: "adv2a_item_16",
    heading: "Item 16 — Investment Discretion",
    regRef: "Form ADV Part 2A Item 16",
    summary: "Discretionary authority and any limits.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `The Adviser has discretionary authority to determine the securities and amounts to be bought and sold for each fund, subject to the investment objectives, restrictions, and limitations set out in the fund's governing documents. ` +
      `[CCO: describe any limitations on the Adviser's discretionary authority.]`,
  },
  {
    key: "adv2a_item_17",
    heading: "Item 17 — Voting Client Securities",
    regRef: "Form ADV Part 2A Item 17",
    summary: "Proxy-voting authority and procedures (or a statement of none).",
    derivation: "ai",
    applies: ALWAYS,
    draft: (p) =>
      p.votesProxies
        ? `Where the Adviser has authority to vote securities held by the funds, it votes in the funds' best interest under written proxy-voting procedures that address conflicts of interest, and investors may obtain a copy of the procedures and the Adviser's voting record on request. [CCO: confirm whether voting is in-house or via a proxy advisor.]`
        : `The Adviser does not have authority to vote securities on behalf of the funds; where votes arise, they are handled as provided in the governing documents. [CCO: confirm this remains accurate.]`,
  },
  {
    key: "adv2a_item_18",
    heading: "Item 18 — Financial Information",
    regRef: "Form ADV Part 2A Item 18",
    summary: "Financial conditions reasonably likely to impair the Adviser.",
    derivation: "ai",
    applies: ALWAYS,
    draft: () =>
      `The Adviser is not aware of any financial condition reasonably likely to impair its ability to meet contractual commitments to clients and has not been the subject of a bankruptcy petition. ` +
      `[CCO: confirm, and disclose any prepayment of fees more than six months in advance if applicable.]`,
  },
] as const;

/** The Form ADV Part 2A brochure items with applicability resolved (no prose). */
export function buildBrochureSections(profile: FirmProfile): ResolvedSection[] {
  return buildSectionsFromModel(ADV_2A_SECTIONS, profile);
}

/** Tailor the Form ADV Part 2A brochure items to the firm (deterministic draft). */
export function tailorBrochure(profile: FirmProfile): SectionDraftRow[] {
  return tailorFromModel(ADV_2A_SECTIONS, profile);
}

/** The manual section keys a given Part 2A item should stay aligned with. */
export function alignedManualSections(itemKey: string): string[] {
  return ADV_2A_ALIGNMENT[itemKey] ?? [];
}
